import React, { useState, useEffect } from "react";
import Select from "react-select";
import OrganizationView from "./OrganizationView";
import AccountDetailView from "./AccountDetailView";
import SecurityTenantView from "../security/SecurityTenantView";
import SecurityOrgView from "../security/SecurityOrgView";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { Spinner } from "react-bootstrap";
import "bootstrap/dist/css/bootstrap.min.css";

const SecureGuard = ({ isSidebarOpen }) => {
  const navigate = useNavigate();
  const [connectedAccounts, setConnectedAccounts] = useState([]);
  const [cloudAccounts, setCloudAccounts] = useState([]);
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [selectedRegion, setSelectedRegion] = useState(null);
  const [isScanning, setIsScanning] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const [trendsData, setTrendsData] = useState(null);
  const [detailsData, setDetailsData] = useState(null);
  const [summaryData, setSummaryData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);
  const [isLoadingAccounts, setIsLoadingAccounts] = useState(true);
  
  const [selectedAccountId, setSelectedAccountId] = useState(null);
  const [tenantID, setTenantID] = useState(null);
  const [tenantData, setTenantData] = useState(null);
  const [selectedOrgId, setSelectedOrgId] = useState(null);
  const [viewMode, setViewMode] = useState('tenant'); // 'tenant', 'organization', 'account'
  const [loadingText, setLoadingText] = useState('Loading SecureGuard...');

  useEffect(() => {
    sessionStorage.removeItem('securityFindings');
    sessionStorage.removeItem('securityAccounts');
    
    const fetchConnectedAccounts = async () => {
      try {
        const tenantID = localStorage.getItem("tenantID");
        const companyID = localStorage.getItem('companyID');
        const userType = localStorage.getItem('userType');
        
        // Check if this is a company user
        const isCompanyUser = userType === 'company' && companyID;
        
        if (isCompanyUser) {
          // For company users, get accounts from MSP API
          const companyResponse = await fetch(
            `https://htb4wmezrg.execute-api.us-west-2.amazonaws.com/prod/v2/msp/companies/${companyID}/config?companyID=${companyID}`
          );
          
          if (!companyResponse.ok) throw new Error("Failed to fetch company accounts");
          
          const companyData = await companyResponse.json();
          const accounts = (companyData.accounts || []).map(acc => ({
            id: acc.accountId,
            alias: acc.accountName,
            iamrole: ""
          }));
          
          setConnectedAccounts(accounts);
          setTenantID(companyID); // Use companyID as effective tenant ID
          
          // For company users, create minimal tenant data structure
          setTenantData({
            tenantName: companyData.companyName || 'Company Portal',
            cloudAccounts: {
              organizations: [],
              aws: companyData.accounts || []
            }
          });
        } else {
          // For tenant users, use existing logic
          if (!tenantID) return;

          const tenantResponse = await fetch(
            `https://u3lrzohpy7.execute-api.us-west-2.amazonaws.com/prod/accounts?tenantID=${tenantID}`
          );

          if (!tenantResponse.ok) throw new Error("Failed to fetch accounts");

          const data = await tenantResponse.json();
          const accounts = (data.accounts || []).map(acc => ({
            id: acc.awsAccountId,
            alias: acc.alias,
            iamrole: ""
          }));
          
          setConnectedAccounts(accounts);
          setTenantID(tenantID);
          
          // Fetch tenant settings for organization structure
          const settingsResponse = await fetch(
            `https://htb4wmezrg.execute-api.us-west-2.amazonaws.com/prod/v2/tenant/settings?tenantID=${tenantID}`
          );
          
          if (settingsResponse.ok) {
            const settingsData = await settingsResponse.json();
            const tenant = settingsData || {};
            
            // Parse cloudAccounts if it's a string
            if (tenant.cloudAccounts && typeof tenant.cloudAccounts === 'string') {
              try {
                tenant.cloudAccounts = JSON.parse(tenant.cloudAccounts);
              } catch (e) {
                console.error('Error parsing cloudAccounts:', e);
                tenant.cloudAccounts = { organizations: [], aws: [] };
              }
            }
            setTenantData(tenant);
            console.log('Tenant data:', tenant);
          }
        }
      } catch (error) {
        console.error("Error fetching connected accounts:", error);
        setConnectedAccounts([]);
      } finally {
        setIsLoadingAccounts(false);
      }
    };
    
    fetchConnectedAccounts();
  }, []);
  
  useEffect(() => {
    const fetchAccounts = async () => {
      try {
        const tenantID = localStorage.getItem("tenantID");
        const companyID = localStorage.getItem('companyID');
        const userType = localStorage.getItem('userType');
        
        // Check if this is a company user
        const isCompanyUser = userType === 'company' && companyID;
        
        if (isCompanyUser) {
          // For company users, get accounts from MSP API
          const companyResponse = await fetch(
            `https://htb4wmezrg.execute-api.us-west-2.amazonaws.com/prod/v2/msp/companies/${companyID}/config?companyID=${companyID}`
          );
          
          if (!companyResponse.ok) throw new Error("Failed to fetch company accounts");
          
          const companyData = await companyResponse.json();
          const accounts = (companyData.accounts || []).map(acc => ({
            awsAccountId: acc.accountId,
            alias: acc.accountName
          }));
          
          setCloudAccounts(accounts);
        } else {
          // For tenant users, use existing logic
          if (!tenantID) {
            console.error("Tenant ID is missing in localStorage");
            return;
          }
          
          const response = await fetch(
            `https://u3lrzohpy7.execute-api.us-west-2.amazonaws.com/prod/accounts?tenantID=${tenantID}`
          );
          
          if (!response.ok) throw new Error("Failed to fetch accounts");
          
          const data = await response.json();
          setCloudAccounts(data.accounts || []);
        }
      } catch (error) {
        console.error("Error fetching accounts:", error);
      }
    };
    
    fetchAccounts();
  }, []);

  useEffect(() => {
    if (!tenantID) {
      return;
    }

    const fetchSecurityData = async () => {
      try {
        const companyID = localStorage.getItem('companyID');
        const userType = localStorage.getItem('userType');
        
        // BACKWARD COMPATIBLE: Support both tenant and company users
        const isCompanyUser = userType === 'company' && companyID;
        const requestBody = isCompanyUser ? { companyID } : { tenantID };
        
        // Fetch summary data
        const summaryResponse = await fetch(
          "https://htb4wmezrg.execute-api.us-west-2.amazonaws.com/prod/v2/security/summary",
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(requestBody),
          }
        );
        if (!summaryResponse.ok) {
          console.error('Summary API Error:', summaryResponse.status, summaryResponse.statusText);
          const errorText = await summaryResponse.text();
          console.error('Summary API Error Body:', errorText);
          throw new Error(`Summary API failed: ${summaryResponse.status}`);
        }
        const summaryData = await summaryResponse.json();
        console.log('Security Summary API Response:', summaryData);
        setSummaryData(summaryData);

        // Fetch all details for tenant view
        const detailsResponse = await fetch(
          "https://htb4wmezrg.execute-api.us-west-2.amazonaws.com/prod/v2/security/details",
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(requestBody),
          }
        );
        if (!detailsResponse.ok) {
          console.error('Details API Error:', detailsResponse.status, detailsResponse.statusText);
          const errorText = await detailsResponse.text();
          console.error('Details API Error Body:', errorText);
          throw new Error(`Details API failed: ${detailsResponse.status}`);
        }
        const detailsData = await detailsResponse.json();
        console.log('Security Details API Response:', detailsData);
        const safeDetailsData = detailsData.findings ? detailsData.findings : (Array.isArray(detailsData) ? detailsData : []);
        setDetailsData(safeDetailsData);
        
        // Fetch trends data
        const trendsResponse = await fetch(
          "https://htb4wmezrg.execute-api.us-west-2.amazonaws.com/prod/v2/security/trends",
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(requestBody),
          }
        );
        
        if (trendsResponse.ok) {
          const trendsData = await trendsResponse.json();
          console.log('Security Trends API Response:', trendsData);
          setTrendsData(trendsData);
        } else {
          console.error('Trends API failed, using summary data');
          setTrendsData([summaryData]);
        }
      } catch (error) {
        console.error("Error fetching security data:", error);
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "Failed to fetch security data. Please try again later.",
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchSecurityData();
  }, [tenantID]);

  // Rotating loading messages for better UX
  useEffect(() => {
    if (isLoading || isLoadingAccounts) {
      const messages = [
        'Loading SecureGuard...',
        'Fetching security data...',
        'Analyzing security findings...',
        'Scanning cloud resources...',
        'Preparing your security dashboard...'
      ];
      let index = 0;
      const interval = setInterval(() => {
        index = (index + 1) % messages.length;
        setLoadingText(messages[index]);
      }, 1500);
      return () => clearInterval(interval);
    }
  }, [isLoading, isLoadingAccounts]);
  
  const handleAccountSelect = async (accountId) => {
    if (!tenantID) return;
    
    setIsLoadingDetails(true);
    setSelectedAccountId(accountId);
    
    try {
      const companyID = localStorage.getItem('companyID');
      const userType = localStorage.getItem('userType');
      
      // BACKWARD COMPATIBLE: Support both tenant and company users
      const isCompanyUser = userType === 'company' && companyID;
      const requestBody = isCompanyUser 
        ? { companyID, accountId } 
        : { tenantID, accountId };
      
      const detailsResponse = await fetch(
        "https://htb4wmezrg.execute-api.us-west-2.amazonaws.com/prod/v2/security/details",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(requestBody),
        }
      );
      
      const detailsData = await detailsResponse.json();
      const safeDetailsData = detailsData.findings ? detailsData.findings : (Array.isArray(detailsData) ? detailsData : []);
      setDetailsData(safeDetailsData);
    } catch (error) {
      console.error("Error fetching details:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to fetch findings. Please try again.",
      });
    } finally {
      setIsLoadingDetails(false);
    }
  };
  
  const handleBackToOrganization = () => {
    setSelectedAccountId(null);
    setViewMode('organization');
  };

  const handleAccountSelectFromOrg = (accountId) => {
    setSelectedAccountId(accountId);
    setViewMode('account');
  };

  const handleOrgSelect = (orgId) => {
    setSelectedOrgId(orgId);
    setViewMode('organization');
  };

  const handleStandaloneSelect = () => {
    setSelectedOrgId('standalone');
    setViewMode('organization');
  };

  const handleBackToTenant = () => {
    setSelectedAccountId(null);
    setSelectedOrgId(null);
    setViewMode('tenant');
  };

  const handleConnectAccount = () => {
    navigate("/dashboard/connected-accounts");
  };

  const handleScan = async () => {
    if (!tenantID) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Tenant ID is missing. Please log in again.",
      });
      return;
    }

    setIsScanning(true);

    try {
      const companyID = localStorage.getItem('companyID');
      const userType = localStorage.getItem('userType');
      
      // BACKWARD COMPATIBLE: Support both tenant and company users
      const isCompanyUser = userType === 'company' && companyID;
      const body = isCompanyUser 
        ? { companyID, cloudRegion: "us-west-2" }
        : { tenantID, cloudRegion: "us-west-2" };
      
      const response = await fetch(
        "https://u56vbbwsfd.execute-api.us-west-2.amazonaws.com/prod/trigger-prowler-scan",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        Swal.fire({
          icon: "error",
          title: "Scan Failed",
          text: errorData.message || "Unknown error occurred.",
        });
        return;
      }

      Swal.fire({
        icon: "success",
        title: "Success",
        text: "Tenant scan started successfully. This may take a few minutes depending on the number of accounts and regions.",
      });
      setIsModalOpen(false);
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "An unexpected error occurred while starting the scan.",
      });
    } finally {
      setIsScanning(false);
    }
  };

  if (isLoadingAccounts) {
    return (
      <div 
        className="cloudmap-page container-fluid pb-5"
        style={{
          transition: "margin-left 0.3s ease",
          marginLeft: isSidebarOpen ? "0px" : "0",
        }}
      >
        <div className="d-flex flex-column justify-content-center align-items-center" style={{ minHeight: 'calc(100vh - 120px)', paddingTop: '80px' }}>
          <Spinner animation="border" variant="primary" style={{ width: '3rem', height: '3rem' }} />
          <p className="mt-3" style={{ color: '#342ead', fontSize: '1.1rem' }}>{loadingText}</p>
        </div>
      </div>
    );
  }

  if (connectedAccounts.length === 0) {
    return (
      <div
        className="d-flex justify-content-center align-items-center vh-100"
        style={{
          backgroundImage: 'url("/images/blurred_image.png")',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
        }}
      >
        <div className="text-center text-dark p-4" style={{ backgroundColor: 'rgba(170, 172, 242, 0.33)', borderRadius: '8px' }}>
          <h3>No Connected Accounts Found</h3>
          <p>
            To access this dashboard, please connect at least one account. Click the button below to get started.
          </p>
          <button className="btn text-light mt-3" style={{ backgroundColor: '#342ead' }} onClick={handleConnectAccount}>
            Connect Account
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      className="cloudmap-page container-fluid pb-5"
      style={{
        transition: "margin-left 0.3s ease",
        marginLeft: isSidebarOpen ? "0px" : "0",
      }}
    >
      {isLoading ? (
        <div className="d-flex flex-column justify-content-center align-items-center" style={{ minHeight: 'calc(100vh - 120px)', paddingTop: '80px' }}>
          <Spinner animation="border" variant="primary" style={{ width: '3rem', height: '3rem' }} />
          <p className="mt-3" style={{ color: '#342ead', fontSize: '1.1rem' }}>{loadingText}</p>
        </div>
      ) : (
        <div className="container">
          <div className="d-flex justify-content-between align-items-center mb-4">
            <div>
              <h2 style={{ fontWeight: '600', color: '#1e293b', marginBottom: '4px' }}>SecureGuard</h2>
              <p style={{ fontSize: '0.875rem', color: '#64748b', marginBottom: 0 }}>
                {selectedAccountId 
                  ? 'Detailed security findings and compliance for individual account'
                  : 'Advanced security monitoring and compliance management across all AWS accounts'
                }
              </p>
            </div>
            <button
              className="btn"
              style={{
                backgroundColor: '#6D5BFF',
                color: 'white',
                fontWeight: '600',
                padding: '10px 24px',
                borderRadius: '8px',
                border: 'none'
              }}
              onClick={handleScan}
              disabled={isScanning}
            >
              {isScanning ? 'Scanning...' : 'Scan'}
            </button>
          </div>

          {isLoadingDetails ? (
            <div className="d-flex flex-column justify-content-center align-items-center" style={{ height: "50vh" }}>
              <div className="spinner-border" style={{ color: '#6D5BFF', width: '3rem', height: '3rem' }} role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="mt-3" style={{ color: '#342ead', fontSize: '1.1rem', fontWeight: 'bold' }}>Loading data, please wait...</p>
            </div>
          ) : !tenantData ? (
            <div className="d-flex flex-column justify-content-center align-items-center" style={{ height: "50vh" }}>
              <div className="spinner-border" style={{ color: '#6D5BFF', width: '3rem', height: '3rem' }} role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="mt-3" style={{ color: '#342ead', fontSize: '1.1rem', fontWeight: 'bold' }}>Loading data, please wait...</p>
            </div>
          ) : selectedAccountId ? (
            <AccountDetailView
              accountId={selectedAccountId}
              detailsData={detailsData}
              summaryData={summaryData}
              cloudAccounts={cloudAccounts}
              onBack={handleBackToOrganization}
            />
          ) : viewMode === 'tenant' ? (
            <SecurityTenantView
              securityData={detailsData}
              summaryData={summaryData}
              tenant={tenantData}
              onOrgSelect={handleOrgSelect}
              onStandaloneSelect={handleStandaloneSelect}
            />
          ) : viewMode === 'organization' ? (
            <SecurityOrgView
              orgId={selectedOrgId}
              orgData={selectedOrgId === 'standalone' ? { alias: 'Standalone Accounts' } : tenantData?.cloudAccounts?.organizations?.find(org => org.orgId === selectedOrgId)}
              accountBreakdown={selectedOrgId === 'standalone' ? 
                summaryData?.accountBreakdown?.filter(account => {
                  const accountId = account.accountId;
                  const orgs = tenantData?.cloudAccounts?.organizations || [];
                  return !orgs.some(org => 
                    org.id === accountId || org.memberAccounts?.some(m => m.accountId === accountId)
                  );
                }) : summaryData?.accountBreakdown
              }
              trendsData={trendsData}
              onBack={handleBackToTenant}
              onAccountSelect={handleAccountSelectFromOrg}
            />
          ) : (
            <OrganizationView
              trendsData={trendsData}
              cloudAccounts={cloudAccounts}
              onAccountSelect={handleAccountSelect}
              onBack={handleBackToTenant}
              selectedOrgId={selectedOrgId}
            />
          )}
        </div>
      )}


    </div>
  );
};

export default SecureGuard;
